/**
 * Serviço de dados partilhado da plataforma E4IU (portais de associações e engenheiros).
 *
 * Espelha a lógica das apps móveis (FirebaseHelper + ProjectService + EmailService)
 * para o browser, usando o mesmo projeto Firebase e a mesma estrutura RTDB:
 *   - Association          (registos das associações)
 *   - User                 (registos dos engenheiros)
 *   - Projects             (projetos; campo "Association" = nome da associação)
 *   - Applications/{projectId}/{userId}        (candidaturas pendentes)
 *   - ApprovedApplications/{projectId}/{userId}(candidaturas aceites)
 *   - emails_to_send       (fila de emails; um processo externo envia)
 *   - Storage: Association/{NIF}.jpg, User/{uid}.jpg, Projects/{id}.jpg
 *
 * Nota: apesar do nome (histórico), é a camada de dados comum da plataforma;
 * o portal de engenheiros (engineerService.js) reutiliza os helpers de baixo
 * nível exportados aqui.
 */
import {
    get, ref, push, set, update, remove, child,
} from "firebase/database";
import {
    getDownloadURL, ref as storageRef, uploadBytes,
} from "firebase/storage";
import { db, storage } from "../firebase";

// Imagens por omissão (as mesmas que as apps usam como fallback)
export const DEFAULT_ASSOCIATION_LOGO =
    "https://firebasestorage.googleapis.com/v0/b/engenheirossemfronteiros.appspot.com/o/Association%2Fordem_engenheiros_portugal.png?alt=media&token=b4d1d230-c36b-4081-9e41-1cd0e6f3a962";
export const DEFAULT_PROJECT_LOGO =
    "https://firebasestorage.googleapis.com/v0/b/engenheirossemfronteiros.appspot.com/o/Projects%2Fdefaultprojectpic.jpg?alt=media&token=0a710ebd-6ac0-4d76-9801-1a86d842e62d";

// Lista de colégios da engenharia (fonte única — a mesma dos pickers das apps).
// Usada pelo registo de engenheiros e pelo "adicionar projeto" das associações.
export const COLLEGES = [
    "Aeronáutica e Aeroespacial",
    "Agronómica",
    "Alimentar",
    "Ambiente",
    "Biomédica",
    "Civil",
    "Eletrotécnica",
    "Florestal",
    "Geográfica",
    "Geológica e de Minas",
    "Gestão Industrial",
    "Informática",
    "Materiais",
    "Mecânica",
    "Naval",
    "Química e Biológica",
];

// ---------- helpers ----------

export async function fetchNode(path) {
    const snapshot = await get(ref(db, path));
    return snapshot.exists() ? snapshot.val() : null;
}

/**
 * Equivalente ao post() do SDK Node.js (o SDK web não tem post).
 * Gera uma chave push e grava o valor nessa chave.
 *
 * NOTA: no browser, "firebase/database" resolve para o SDK MODULAR (ESM),
 * onde `ref()` só aceita uma Database como 1.º argumento. Como `parentRef`
 * é uma Reference, usamos `child()` (que aceita Reference em ambas as APIs).
 * Usar `ref(parentRef, key)` aqui lançaria "db._checkNotDeleted is not a function".
 */
export async function postValue(parentRef, value) {
    const key = push(parentRef).key;
    await set(child(parentRef, key), value);
    return key;
}

/** Procura um registo num nó (Association/User) pelo email. Devolve { key, ...dados } ou null. */
export async function findRecordByEmail(nodeName, email) {
    const data = await fetchNode(nodeName);
    if (!data) return null;
    const match = Object.entries(data).find(
        ([, rec]) => rec && String(rec.Email || rec.email || "").toLowerCase() === email.toLowerCase()
    );
    return match ? { key: match[0], ...match[1] } : null;
}

/** Procura um registo num nó pelo campo Id. Devolve { key, ...dados } ou null. */
export async function findRecordById(nodeName, id) {
    const data = await fetchNode(nodeName);
    if (!data) return null;
    const match = Object.entries(data).find(([, rec]) => rec && rec.Id === id);
    return match ? { key: match[0], ...match[1] } : null;
}

/** Formata a data como as apps: dd/MM/yyyy HH:mm:ss */
export function nowFormatted() {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/**
 * Converte erros do Firebase em mensagens em português acionáveis.
 * Devolve null quando não reconhece o erro (o chamador usa o fallback genérico).
 */
export function friendlyErrorMessage(err) {
    const code = (err && err.code) || "";
    switch (code) {
        case "auth/invalid-email":
            return "O email indicado não é válido.";
        case "auth/weak-password":
            return "A palavra-passe é demasiado fraca. Usa pelo menos 6 caracteres, incluindo pelo menos um número.";
        case "auth/email-already-in-use":
            return "Este email já está registado. Se é teu, usa 'Iniciar sessão' em vez de 'Registar'.";
        case "auth/user-disabled":
            return "Esta conta foi desativada. Contacta esf@centro.oep.pt.";
        case "auth/too-many-requests":
            return "Muitas tentativas seguidas. Espera alguns minutos e volta a tentar.";
        case "auth/no-current-user":
            return "Não existe uma sessão ativa para enviar o email de verificação. Tenta novamente.";
        case "auth/unauthorized-domain":
            return "Este site não está autorizado no Firebase. Contacta esf@centro.oep.pt.";
        case "auth/network-request-failed":
            return "Sem ligação à internet (ou o pedido falhou). Verifica a rede e volta a tentar.";
        case "storage/unauthorized":
            return "Não foi permitido aceder ao armazenamento de fotos. Contacta esf@centro.oep.pt.";
        case "storage/bucket-not-found":
        case "storage/project-not-found":
            return "O armazenamento de fotos não está acessível. Contacta esf@centro.oep.pt.";
        case "storage/unavailable":
        case "storage/total-quota-exceeded":
            return "O armazenamento de fotos está temporariamente indisponível. Tenta mais tarde ou contacta esf@centro.oep.pt.";
    }
    // Erros do Storage com código HTTP (ex.: 402 = faturação/cota do projeto no Firebase)
    if (err && err.serverResponseCode) {
        return `O armazenamento de fotos devolveu o erro ${err.serverResponseCode}. Contacta esf@centro.oep.pt.`;
    }
    return null;
}

// ---------- emails (mesma fila das apps) ----------

export async function queueEmail(to, subject, body) {
    await postValue(ref(db, "emails_to_send"), {
        to,
        subject,
        body,
        timestamp: new Date().toISOString(),
    });
}

// ---------- associações ----------

export async function getAssociationByEmail(email) {
    return findRecordByEmail("Association", email);
}

export async function getEngineerByEmail(email) {
    return findRecordByEmail("User", email);
}

export async function addAssociationRecord(association) {
    const refKey = await postValue(ref(db, "Association"), association);
    return refKey;
}

export async function updateAssociation(key, data) {
    await set(ref(db, `Association/${key}`), data);
}

/**
 * Carrega a foto da associação para Association/{NIF}.jpg (mesmo caminho da app)
 * e devolve a URL de download.
 */
export async function uploadAssociationPhoto(nif, imageFile) {
    const sref = storageRef(storage, `Association/${nif}.jpg`);
    await uploadBytes(sref, imageFile);
    return await getDownloadURL(sref);
}

// ---------- projetos ----------

export async function getAllProjects() {
    const data = await fetchNode("Projects");
    if (!data) return [];
    return Object.entries(data).map(([key, p]) => ({ key, ...p }));
}

/** Projetos da associação (campo Association == nome) e com Enabled — igual à app. */
export async function getAssociationProjects(associationName) {
    const all = await getAllProjects();
    return all.filter((p) => p.Association === associationName && p.Enabled);
}

/** Pesquisa dentro dos projetos da associação (título, localização, curso) — igual à app. */
export async function searchProjects(associationName, search) {
    const all = await getAssociationProjects(associationName);
    const s = (search || "").toLowerCase();
    if (!s) return all;
    return all.filter((p) =>
        String(p.Title || "").toLowerCase().includes(s) ||
        String(p.Location || "").toLowerCase().includes(s) ||
        String(p.College || "").toLowerCase().includes(s)
    );
}

/** Projeto pelo Id (funciona para projetos antigos e novos). */
export async function getProject(id) {
    return findRecordById("Projects", id);
}

/**
 * Cria projeto + upload da imagem.
 * Gera um id push (sem colisões, ao contrário do aleatório de 6 dígitos da app).
 *
 * Se o upload da imagem falhar (ex.: storage do Firebase indisponível),
 * o projeto é criado na mesma com a imagem por omissão — igual ao registo
 * de associações (o registo não fica bloqueado por um problema de storage).
 * Devolve { id, photoFallback }; photoFallback = true quando houve imagem
 * mas o upload falhou (para a página avisar o utilizador).
 */
export async function addProjectWithImage(project, imageFile) {
    const idRef = push(ref(db, "Projects"));
    const id = idRef.key;

    let logo = DEFAULT_PROJECT_LOGO;
    let photoFallback = false;
    if (imageFile) {
        try {
            const sref = storageRef(storage, `Projects/${id}.jpg`);
            await uploadBytes(sref, imageFile);
            logo = await getDownloadURL(sref);
        } catch (err) {
            // Falha no upload (ex.: erro de faturação/cota do Storage):
            // usamos a imagem por omissão para não bloquear a criação do projeto.
            console.error("Falha no upload da foto do projeto; a usar a imagem por omissão.", err);
            photoFallback = true;
        }
    }

    const record = { ...project, Id: id, Logo: logo, Enabled: false };
    await set(idRef, record);
    return { id, photoFallback };
}

/** Apaga projeto + candidaturas + imagem (igual à app). */
export async function deleteProject(project) {
    const pid = project.Id;
    await remove(ref(db, `Applications/${pid}`));
    await remove(ref(db, `ApprovedApplications/${pid}`));
    await remove(ref(db, `Projects/${project.key}`));
    try {
        await remove(storageRef(storage, `Projects/${pid}.jpg`));
    } catch { /* imagem pode não existir */ }
}

/**
 * Conclui projeto: +1 ponto aos aprovados (nós ApprovedApplications e User)
 * e email a cada um (a app usa push; no browser usamos a fila de emails).
 * Depois apaga o projeto e as candidaturas (igual à app).
 */
export async function finishProject(project) {
    const pid = project.Id;
    const approved = (await fetchNode(`ApprovedApplications/${pid}`)) || {};

    for (const [userId, user] of Object.entries(approved)) {
        const newPoints = (user.Points || 0) + 1;

        await update(ref(db, `ApprovedApplications/${pid}/${userId}`), { Points: newPoints });

        const engineer = await findRecordById("User", userId);
        if (engineer) {
            await update(ref(db, `User/${engineer.key}`), { Points: newPoints });
        }

        await queueEmail(
            user.Email,
            "E4IU",
            `O projeto ${project.Title} foi dado como concluído. Recebeu 1 ponto!`
        );
    }

    await remove(ref(db, `Applications/${pid}`));
    await remove(ref(db, `ApprovedApplications/${pid}`));
    await remove(ref(db, `Projects/${project.key}`));
    try {
        await remove(storageRef(storage, `Projects/${pid}.jpg`));
    } catch { /* imagem pode não existir */ }
}

// ---------- candidaturas ----------

export async function getApplications(projectId) {
    const data = await fetchNode(`Applications/${projectId}`);
    if (!data) return [];
    return Object.entries(data).map(([key, u]) => ({ key, ...u }));
}

export async function getApprovedApplications(projectId) {
    const data = await fetchNode(`ApprovedApplications/${projectId}`);
    if (!data) return [];
    return Object.entries(data).map(([key, u]) => ({ key, ...u }));
}

/** Aceita candidatura: move para ApprovedApplications + email (textos iguais à app). */
export async function acceptApplication(applicant, projectId) {
    await set(ref(db, `ApprovedApplications/${projectId}/${applicant.Id}`), applicant);
    await remove(ref(db, `Applications/${projectId}/${applicant.Id}`));

    await queueEmail(
        applicant.Email,
        "E4IU - Candidatura Aceite!",
        `Olá ${applicant.FirstName} ${applicant.LastName},\n\nParabéns! A tua candidatura para o projeto '${applicant._projectTitle || ""}' foi aceite pela associação.\n\nA associação irá entrar em contacto contigo em breve para combinar os próximos passos.`
    );
}

/** Rejeita candidatura: remove + email (textos iguais à app). */
export async function rejectApplication(applicant, projectId) {
    await remove(ref(db, `Applications/${projectId}/${applicant.Id}`));

    await queueEmail(
        applicant.Email,
        "E4IU - Atualização da Candidatura",
        `Olá ${applicant.FirstName} ${applicant.LastName},\n\nInfelizmente, a tua candidatura para o projeto '${applicant._projectTitle || ""}' não foi aceite.\n\nAgradecemos o teu interesse e encorajamos-te a candidatar a outros projetos na plataforma E4IU.`
    );
}

/** Remove engenheiro já aprovado: remove + email (textos iguais à app). */
export async function removeApprovedApplication(applicant, projectId) {
    await remove(ref(db, `ApprovedApplications/${projectId}/${applicant.Id}`));

    await queueEmail(
        applicant.Email,
        "Remoção de Projeto",
        `Olá ${applicant.FirstName} ${applicant.LastName},\n\nInformamos que foste removido do projeto '${applicant._projectTitle || ""}'.\n\nPara mais esclarecimentos, podes contactar a associação responsável pelo projeto.`
    );
}
